/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth-store'
import { useAuthModalStore } from '@/stores/auth-modal-store'
import { useStatus } from '@/hooks/use-status'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { UserAuthForm } from '@/features/auth/sign-in/components/user-auth-form'
import { SignUpForm } from '@/features/auth/sign-up/components/sign-up-form'
import { ForgotPasswordForm } from '@/features/auth/forgot-password/components/forgot-password-form'
import { TermsFooter } from './terms-footer'

export function AuthModal() {
  const { t } = useTranslation()
  const { open, view, setOpen, setView } = useAuthModalStore()
  const { auth } = useAuthStore()
  const { status } = useStatus()

  // Auto-close modal when user logs in successfully
  useEffect(() => {
    if (open && auth.user) {
      setOpen(false)
    }
  }, [open, auth.user, setOpen])

  const handleSwitchView = (newView: typeof view) => {
    setView(newView)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='sm:max-w-md max-h-[90vh] overflow-y-auto p-0 gap-0'>
        <DialogHeader className='px-6 pt-6 pb-2'>
          <DialogTitle className='text-center text-2xl font-semibold tracking-tight'>
            {view === 'sign-in' && t('Sign in')}
            {view === 'sign-up' && t('Create an account')}
            {view === 'forgot-password' && t('Forgot password')}
          </DialogTitle>
        </DialogHeader>

        <div className='px-6 pb-6 space-y-4'>
          {view === 'sign-in' && (
            <>
              {!status?.self_use_mode_enabled && status?.register_enabled !== false && (
                <p className='text-muted-foreground text-sm text-center'>
                  {t("Don't have an account?")}{' '}
                  <button
                    type='button'
                    onClick={() => handleSwitchView('sign-up')}
                    className='hover:text-primary font-medium underline underline-offset-4'
                  >
                    {t('Sign up')}
                  </button>
                </p>
              )}
              <UserAuthForm
                redirectTo='/'
                onForgotPassword={() => handleSwitchView('forgot-password')}
              />
              <TermsFooter variant='sign-in' status={status} className='text-center' />
            </>
          )}

          {view === 'sign-up' && (
            <>
              <p className='text-muted-foreground text-sm text-center'>
                {t('Already have an account?')}{' '}
                <button
                  type='button'
                  onClick={() => handleSwitchView('sign-in')}
                  className='hover:text-primary font-medium underline underline-offset-4'
                >
                  {t('Sign in')}
                </button>
              </p>
              <SignUpForm />
              <TermsFooter variant='sign-up' status={status} className='text-center' />
            </>
          )}

          {view === 'forgot-password' && (
            <>
              <p className='text-muted-foreground text-sm text-center'>
                {t('Remember your password?')}{' '}
                <button
                  type='button'
                  onClick={() => handleSwitchView('sign-in')}
                  className='hover:text-primary font-medium underline underline-offset-4'
                >
                  {t('Sign in')}
                </button>
              </p>
              <ForgotPasswordForm />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
